import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';
import { track } from '../lib/track';
import { trackEvent } from '../lib/analytics';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const session = useSession();
  const [isLoading, setIsLoading] = useState(true);
  const [activationStatus, setActivationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const sessionId = searchParams.get('session_id');
  const planName = searchParams.get('plan') || searchParams.get('plan_name');
  const trackedSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    const processPaymentSuccess = async () => {
      // Set flag that payment was successful (for CalendarSubscription to detect)
      sessionStorage.setItem('payment_success', 'true');
      
      // Clear sessionStorage
      sessionStorage.removeItem('pending_camp_slug');
      sessionStorage.removeItem('pending_plan_name');
      sessionStorage.removeItem('pending_claim_camp_slug');
      sessionStorage.removeItem('pending_claim_camp_id');
      sessionStorage.removeItem('stripe_checkout_initiated');

      // If we have a session ID and user is logged in, try to manually activate
      // We're on /payment/success, so this is definitely a parent subscription
      if (sessionId && session?.user) {
        console.log('PaymentSuccessPage: Attempting to activate subscription', { sessionId, planName, userEmail: session.user.email });
        
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const { data: { session: authSession } } = await supabase.auth.getSession();
          
          if (!authSession?.access_token) {
            console.error('No access token available');
            setActivationStatus('error');
            // Still invalidate in case webhook processed it
            await queryClient.invalidateQueries(['subscription']);
            await queryClient.invalidateQueries(['profile']);
            return;
          }
          
          console.log('PaymentSuccessPage: Calling activate-subscription function');
          const response = await fetch(`${supabaseUrl}/functions/v1/activate-subscription`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authSession.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
          });

          console.log('PaymentSuccessPage: Activation response status:', response.status);

          if (response.ok) {
            const result = await response.json();
            console.log('PaymentSuccessPage: Subscription activated successfully:', result);
            setActivationStatus('success');
            if (sessionId && trackedSessionIdRef.current !== sessionId) {
              trackedSessionIdRef.current = sessionId;
              track('purchase_completed', {
                persona: 'parent',
                product_type: planName === 'ltd-parents' ? 'subscription' : 'camp_booking',
                stripe_session_id: sessionId || '',
                order_id: sessionId || '',
                currency: 'USD',
              });
              if (planName === 'ltd-parents') {
                trackEvent('LTD-Parents-Conversion', { plan: 'ltd-parents', session_id: sessionId || '' });
              }
            }
            // Invalidate queries immediately
            await queryClient.invalidateQueries(['subscription']);
            await queryClient.invalidateQueries(['profile']);
          } else {
            const errorText = await response.text();
            let errorData;
            try {
              errorData = JSON.parse(errorText);
            } catch {
              errorData = { message: errorText };
            }
            console.error('PaymentSuccessPage: Failed to activate subscription:', errorData);
            setActivationStatus('error');
            // Still invalidate in case webhook processed it
            await queryClient.invalidateQueries(['subscription']);
            await queryClient.invalidateQueries(['profile']);
          }
        } catch (error: any) {
          console.error('PaymentSuccessPage: Error activating subscription:', error);
          setActivationStatus('error');
          // Still try to invalidate
          await queryClient.invalidateQueries(['subscription']);
          await queryClient.invalidateQueries(['profile']);
        }
      } else {
        console.log('PaymentSuccessPage: No sessionId or user, waiting for webhook', { 
          hasSessionId: !!sessionId, 
          hasUser: !!session?.user,
          planName 
        });
        // Wait a bit for webhook to process, then invalidate
        await new Promise(resolve => setTimeout(resolve, 2000));
        await queryClient.invalidateQueries(['subscription']);
        await queryClient.invalidateQueries(['profile']);
        
        const effectiveSessionId = sessionId || 'webhook-processed';
        if (trackedSessionIdRef.current !== effectiveSessionId) {
          trackedSessionIdRef.current = effectiveSessionId;
          track('purchase_completed', {
            persona: 'parent',
            product_type: planName === 'ltd-parents' ? 'subscription' : 'camp_booking',
            stripe_session_id: sessionId || 'webhook-processed',
            order_id: sessionId || 'webhook-processed',
            currency: 'USD',
          });
          if (planName === 'ltd-parents') {
            trackEvent('LTD-Parents-Conversion', { plan: 'ltd-parents', session_id: sessionId || 'webhook-processed' });
          }
        }
      }

      // Show success page after processing
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1500);

      return () => clearTimeout(timer);
    };

    processPaymentSuccess();
  }, [queryClient, sessionId, session, planName]);

  if (isLoading) {
    return (
      <div className="page-container" style={{ minHeight: 'auto' }}>
        <div className="page-content flex items-center justify-center" style={{ minHeight: '60vh' }}>
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {planName === 'ltd-parents' 
                ? 'Setting up your lifetime access...' 
                : 'Setting up your Family Sync...'}
            </h1>
            <p className="text-gray-600">
              Please wait while we activate your {planName === 'ltd-parents' ? 'lifetime access' : 'subscription'}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ minHeight: 'auto' }}>
      <SEO
        title="Payment Successful - Family Sync Activated | CampWhiz"
        description="Your Family Sync subscription has been activated successfully."
        url="/payment/success"
        noindex={true}
      />
      
      <div className="page-content py-12" style={{ minHeight: 'auto' }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <CheckCircleIcon className="h-24 w-24 text-green-500 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {planName === 'ltd-parents' 
                ? 'Lifetime Access Activated! 🎉' 
                : 'Family Sync Activated! 🎉'}
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {planName === 'ltd-parents' 
                ? 'Your lifetime access has been successfully activated. You now have permanent access to all CampWhiz parent features.' 
                : 'Your Family Sync subscription has been successfully activated. You can now sync and share your kids\' camp schedules with family members.'}
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-green-800 mb-3">
              What's Next?
            </h2>
            <ul className="text-left text-green-700 space-y-2">
              {planName === 'ltd-parents' ? (
                <>
                  <li>✅ Lifetime access to all parent features</li>
                  <li>✅ Save and reuse your camp library every year</li>
                  <li>✅ Build your family camp calendar</li>
                  <li>✅ Never pay again - it's yours forever</li>
                </>
              ) : (
                <>
                  <li>✅ Sync your kids' camp schedules</li>
                  <li>✅ Share calendars with family members</li>
                  <li>✅ Keep everyone in the loop automatically</li>
                  <li>✅ Manage all schedules in one place</li>
                </>
              )}
            </ul>
          </div>

          <div className="space-y-4">
            <Link
              to="/kids-schedule"
              className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Go to Kids Schedule
            </Link>
            <div>
              <Link
                to="/"
                className="text-primary hover:text-primary/80 font-medium"
              >
                Return to Homepage
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

